/**
 * Historial de actividad de clientes — servicio API (round 2026-07-25).
 *
 * - list():                lectura paginada del historial (admin-only en backend).
 * - registrarNavegacion(): beacon fire-and-forget que dispara el
 *   `ClientActivityTracker` en cada cambio de página de un cliente.
 *
 * Todo va por `apiFetch` (auth por cookie, CSRF, toasts). Ver
 * docs/modulo-actividad-clientes.md.
 */

import { apiFetch } from "./apiClient";

// ─── Types ──────────────────────────────────────────────────────────────────

export type ActividadTipo =
  | "login"
  | "navegacion"
  | "consulta"
  | "creacion"
  | "edicion"
  | "eliminacion";

export interface ActividadRecord {
  id: number;
  user_id: number | null;
  user_nombre: string | null;
  user_email: string | null;
  tipo: ActividadTipo;
  metodo: string | null;
  ruta: string | null;
  descripcion: string | null;
  status_code: number | null;
  ip: string | null;
  user_agent: string | null;
  duracion_ms: number | null;
  created_at: string;
}

export interface ActividadListParams {
  page?: number;
  size?: number;
  q?: string;
  tipo?: ActividadTipo;
  from?: string;
  to?: string;
}

export interface ActividadListResponse {
  success: boolean;
  data: ActividadRecord[];
  total: number;
  page: number;
  size: number;
}

// ─── actividadClientesApi ─────────────────────────────────────────────────────

export const actividadClientesApi = {
  /**
   * GET /actividad-clientes — página filtrable del historial. Requiere rol
   * administrador (lo impone el backend).
   */
  list: (
    params?: ActividadListParams,
  ): Promise<ActividadListResponse | null> => {
    const qs = params
      ? "?" +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== "")
            .map(([k, v]) => [k, String(v)]) as [string, string][],
        ).toString()
      : "";
    return apiFetch<ActividadListResponse>(`/actividad-clientes${qs}`, {
      errorMsg: "Error al obtener el historial de actividad",
    });
  },

  /**
   * POST /actividad-clientes/navegacion — registra un cambio de página del
   * cliente. Fire-and-forget: no bloquea la navegación ni muestra toasts.
   * El backend ignora (204) a los roles no-cliente.
   */
  registrarNavegacion: (ruta: string, titulo?: string): void => {
    void apiFetch(`/actividad-clientes/navegacion`, {
      method: "POST",
      body: JSON.stringify({ ruta, titulo }),
      silent: true,
    });
  },
};
